import { api, ApiError } from "./api";

// 権限区分。API の UserRole（lib/enums/user_role.py）と一致させる。
// 強い順に ADMIN > LEADER > OPERATOR > VIEW（上位は下位を含む）。
export const ROLE_RANK = {
  view: 0,
  operator: 1,
  leader: 2,
  admin: 3,
} as const;

export type UserRole = keyof typeof ROLE_RANK;

// 自身の権限が required 以上か（階層包含）。API の UserRole.allows と同じ判定。
export function roleAllows(role: UserRole, required: UserRole): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[required];
}

// 認証済みユーザー。API の `GET /auth` レスポンスに対応する。
export interface AuthUser {
  user_id: string;
  email: string;
  name?: string | null;
  is_admin?: boolean;
  role: UserRole;
  /**
   * 仮パスワードのまま使っている状態。
   *
   * true の間は、API がパスワード変更・自分の情報取得・ログアウト以外を
   * 403 で弾く。画面側も [[AuthGate]] でパスワード変更へ誘導する。
   */
  must_change_password?: boolean;
}

// 現在のログインユーザーを取得する。未ログインは 401（ApiError）で返る。
// 起動時のセッション復元にも使うため、401 で「セッションが切れました」と
// 通知しないよう共通ハンドラを外す（未ログインは正常な経路）。
export function fetchAuthUser(): Promise<AuthUser> {
  return api.get<AuthUser>("/auth", { skipUnauthorizedHandler: true });
}

// ログイン失敗を表す例外（フォームに表示するメッセージ付き）。
export class LoginError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "LoginError";
  }
}

// メールアドレスとパスワードでログインする。成功時は Cookie がセットされる。
export async function login(email: string, password: string): Promise<AuthUser> {
  try {
    // ここの 401 は「メール/パスワードが違う」であってセッション切れではない。
    // 共通ハンドラに渡すと「セッションが切れました」と誤って通知される。
    return await api.post<AuthUser>(
      "/auth/login",
      { email, password },
      { skipUnauthorizedHandler: true },
    );
  } catch (e) {
    if (e instanceof ApiError) {
      if (e.status === 401) {
        throw new LoginError("メールアドレスまたはパスワードが正しくありません。");
      }
      // サーバからのメッセージ(detail)があればそれを、無ければ汎用文言。
      throw new LoginError(
        e.message || "ログインに失敗しました。時間をおいて再度お試しください。",
      );
    }
    throw new LoginError(
      "サーバーに接続できませんでした。通信環境を確認してください。",
    );
  }
}

// ログアウト（サーバ側セッション破棄）。
export function logout(): Promise<void> {
  return api.post<void>("/auth/logout");
}

/**
 * 本人がパスワードを変更する。
 *
 * 現在のパスワードの確認が必要（仮パスワードを受け取った直後はその値）。
 * 変更すると**この端末以外のセッションは失効する**。
 *
 * 現在のパスワードが違う場合は 400 + `INVALID_CURRENT_PASSWORD` が返る（401 では
 * ない）。したがってここで 401 を受けたら本当にセッションが切れているので、
 * 共通ハンドラに任せてよい。
 */
export function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<AuthUser> {
  return api.patch<AuthUser>("/auth/password", {
    current_password: currentPassword,
    new_password: newPassword,
  });
}
